import api from '../api';
import { LoginCredentials, RegisterData, User, ProfileUpdateData, PasswordResetRequest, AuthResponse } from '../types/auth';

export const AuthService = {
    async login(credentials: LoginCredentials): Promise<AuthResponse> {
        const response = await api.post<{ access_token: string; user: User; message?: string }>('/auth/login', credentials);
        const token = response.data.access_token;
        localStorage.setItem('token', token);
        return {
            token,
            user: {
                ...response.data.user,
                token
            },
            message: response.data.message || 'Login successful'
        };
    },

    async register(data: RegisterData): Promise<AuthResponse> {
        const lang = localStorage.getItem('i18nextLng') || 'en';
        const response = await api.post<{ access_token: string; user: User; message?: string }>('/auth/register', { ...data, lang });
        const token = response.data.access_token;
        localStorage.setItem('token', token);
        return {
            token,
            user: {
                ...response.data.user,
                token
            },
            message: response.data.message || 'Registration successful'
        };
    },

    async verifyEmail(token: string, email?: string): Promise<{ message: string }> {
        const lang = localStorage.getItem('i18nextLng') || 'en';
        const url = email 
            ? `/auth/verify-email/${token}?email=${encodeURIComponent(email)}&lang=${lang}`
            : `/auth/verify-email/${token}?lang=${lang}`;
        const response = await api.get(url);
        return response.data;
    },

    async requestPasswordReset(data: PasswordResetRequest): Promise<{ message: string }> {
        const lang = localStorage.getItem('i18nextLng') || 'en';
        const response = await api.post('/auth/forgot-password', { ...data, lang });
        return response.data;
    },

    async resendVerification(email: string): Promise<{ message: string }> {
        const lang = localStorage.getItem('i18nextLng') || 'en';
        const response = await api.post('/auth/resend-verification', { email, lang });
        return response.data;
    },

    async resetPassword(token: string, new_password: string): Promise<{ message: string }> {
        const response = await api.post(`/auth/reset-password/${token}`, { password: new_password });
        return response.data;
    },

    async getCurrentUser(token: string): Promise<AuthResponse> {
        const response = await api.get<User>('/auth/profile', {
            headers: { Authorization: `Bearer ${token}` }
        });
        return {
            token,
            user: {
                ...response.data,
                token
            },
            message: 'User session loaded successfully'
        };
    },

    async updateProfile(data: ProfileUpdateData, token: string): Promise<User> {
        const response = await api.put<User>('/auth/profile', data, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return {
            ...response.data,
            token
        };
    },

    async getProfile(token: string): Promise<AuthResponse> {
        const response = await api.get<User>('/auth/profile', {
            headers: { Authorization: `Bearer ${token}` }
        });
        return {
            token,
            user: {
                ...response.data,
                token
            },
            message: 'Profile loaded successfully'
        };
    },

    logout(): void {
        localStorage.removeItem('token');
    },

    isAuthenticated(): boolean {
        return !!localStorage.getItem('token');
    }
};
